import { memo, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslations } from '@/i18n';
import { useForm, useFormState, useWatch, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  addDays,
  endOfDay,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  startOfDay,
  subDays,
} from 'date-fns';
import { ka } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useCountries } from '@/hooks/useCountries';
import { useCities } from '@/hooks/useCities';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import {
  Select,
  SelectTrigger,
  SelectValue,
  useSelectSearch,
  VirtualizedSelectContent,
  type VirtualizedSelectOption,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { CheckedState } from '@radix-ui/react-checkbox';
import { LOCAL_TRIP_COUNTRY_ID } from '@/config/trips';

function FloatingSelectValue({
  label,
  hasValue,
  placeholder,
  children,
  disabled = false,
}: {
  label: string;
  hasValue: boolean;
  placeholder?: string;
  children?: ReactNode;
  disabled?: boolean;
}) {
  const { searchTerm } = useSelectSearch();
  const isTyping = searchTerm.length > 0;
  const showValue = hasValue || isTyping;
  const labelText = showValue ? label : (placeholder ?? label);
  const displayText = isTyping ? searchTerm : null;

  return (
    <div className="relative w-full">
      <div
        className={cn(
          'pointer-events-none absolute left-0 origin-left transition-all duration-200',
          disabled ? 'text-[#bbbbbb]' : 'text-[#666666]',
          showValue
            ? 'top-0 text-[0.8125rem] leading-4'
            : 'top-1/2 -translate-y-1/2 text-base leading-6'
        )}
      >
        {labelText}
      </div>
      {showValue && (
        <div
          className={cn(
            'flex w-full min-w-0 items-start pt-5 text-base leading-6',
            disabled ? 'text-[#bbbbbb]' : 'text-[#292929]'
          )}
        >
          {isTyping ? (
            <span className="truncate leading-6">{displayText}</span>
          ) : (
            <span className="truncate">
              <SelectValue placeholder={placeholder ?? label}>{children}</SelectValue>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export interface CityFormData {
  countryId: string;
  countryName: string;
  cityId: string;
  cityName: string;
  arrivalDate: Date;
  departureDate: Date;
  needsAccommodation: boolean;
  needsTransport: boolean;
  needsFuel: boolean;
}

interface CityFormFields {
  countryId: string;
  cityId: string;
  arrivalDate: Date | null;
  departureDate: Date | null;
  needsAccommodation: boolean;
  needsTransport: boolean;
  needsFuel: boolean;
}

interface ExistingCityRange {
  id: number;
  arrivalDate: Date;
  departureDate: Date;
  hotelCheckoutDate?: Date | null;
}

interface CityFormProps {
  onSave: (data: CityFormData) => void;
  onCancel: () => void;
  initialData?: CityFormData;
  isLoading?: boolean;
  tripType?: 'local' | 'international';
  tripStartDate?: Date | null;
  tripEndDate?: Date | null;
  otherCities?: ExistingCityRange[];
  permanentResidenceCountryId?: number | null;
  permanentResidenceCityId?: number | null;
  /** External validation: arrival date has issues (e.g., outside trip range) */
  hasArrivalDateIssue?: boolean;
  /** External validation: departure date has issues (e.g., outside trip range) */
  hasDepartureDateIssue?: boolean;
}

type RequirementKey = 'needsAccommodation' | 'needsTransport' | 'needsFuel';

type DisabledReason = 'oneDayTrip' | 'exclusiveField' | null;

interface RequirementConfig {
  key: RequirementKey;
  label: string;
  exclusiveWith?: RequirementKey;
}

interface RequirementCheckboxProps {
  form: UseFormReturn<CityFormFields>;
  config: RequirementConfig;
  isLocalTrip: boolean;
  isAccommodationDisabledForSingleDay: boolean;
  tooltipMessages: {
    oneDayTripNoHotel: string;
    transportFuelExclusive: string;
  };
}

const RequirementCheckbox = memo(function RequirementCheckbox({
  form,
  config,
  isLocalTrip,
  isAccommodationDisabledForSingleDay,
  tooltipMessages,
}: RequirementCheckboxProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const value = Boolean(useWatch({ control: form.control, name: config.key }));
  const exclusiveFieldName = config.exclusiveWith ?? config.key;
  const exclusiveRaw = useWatch({ control: form.control, name: exclusiveFieldName });
  const exclusiveValue = config.exclusiveWith ? Boolean(exclusiveRaw) : false;

  // Determine why the checkbox is disabled
  const disabledReason: DisabledReason = useMemo(() => {
    // Single-day city without hotel connection disables accommodation
    if (config.key === 'needsAccommodation' && isAccommodationDisabledForSingleDay && !value) {
      return 'oneDayTrip';
    }
    // Exclusive field (transport/fuel) is checked
    if (isLocalTrip && config.exclusiveWith && exclusiveValue && !value) {
      return 'exclusiveField';
    }
    return null;
  }, [
    config.key,
    config.exclusiveWith,
    isAccommodationDisabledForSingleDay,
    isLocalTrip,
    exclusiveValue,
    value,
  ]);

  const isDisabled = disabledReason !== null;
  const checkboxId = useId();

  // Get tooltip message based on reason
  const tooltipMessage = useMemo(() => {
    if (disabledReason === 'oneDayTrip') {
      return tooltipMessages.oneDayTripNoHotel;
    }
    if (disabledReason === 'exclusiveField') {
      return tooltipMessages.transportFuelExclusive;
    }
    return null;
  }, [disabledReason, tooltipMessages]);

  // Handle click on the wrapper to show tooltip on mobile
  const handleWrapperClick = (e: React.MouseEvent) => {
    if (isDisabled && tooltipMessage) {
      e.preventDefault();
      e.stopPropagation();
      setTooltipOpen(true);
      // Auto-close tooltip after 2 seconds on mobile
      setTimeout(() => setTooltipOpen(false), 2000);
    }
  };

  const checkboxContent = (
    <FormField
      control={form.control}
      name={config.key}
      render={({ field }) => {
        const applyNext = (next: boolean) => {
          if (isDisabled && next) {
            return;
          }

          if (field.value !== next) {
            field.onChange(next);
          }

          if (next && config.exclusiveWith && isLocalTrip) {
            const otherCurrent = form.getValues(config.exclusiveWith);
            if (otherCurrent) {
              form.setValue(config.exclusiveWith, false, {
                shouldDirty: true,
                shouldTouch: true,
              });
            }
          }
        };

        const handleCheckedChange = (state: CheckedState) => {
          applyNext(state === true);
        };

        return (
          <FormItem className="py-1">
            <div
              className={cn(
                'flex min-h-[2.5rem] items-center gap-2',
                isDisabled && 'cursor-default'
              )}
              onClick={handleWrapperClick}
            >
              <FormControl>
                <Checkbox
                  id={checkboxId}
                  ref={field.ref}
                  checked={value}
                  onCheckedChange={handleCheckedChange}
                  disabled={isDisabled}
                  className={cn(
                    'h-5 w-5 rounded-[4px] border border-[#ebebeb] bg-[#f1f1f1] data-[state=checked]:border-[#292929] data-[state=checked]:bg-[#292929] data-[state=checked]:text-white',
                    isDisabled && 'pointer-events-none'
                  )}
                />
              </FormControl>
              <label
                htmlFor={isDisabled ? undefined : checkboxId}
                className={cn(
                  'text-[15px] leading-6 select-none',
                  isDisabled
                    ? 'pointer-events-none cursor-default text-[#999999]'
                    : 'cursor-pointer text-[#292929]'
                )}
              >
                {config.label}
              </label>
            </div>
          </FormItem>
        );
      }}
    />
  );

  // Wrap with tooltip only when disabled
  if (isDisabled && tooltipMessage) {
    return (
      <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
        <TooltipTrigger asChild>
          <div className="w-fit">{checkboxContent}</div>
        </TooltipTrigger>
        <TooltipContent side="top" align="start">
          {tooltipMessage}
        </TooltipContent>
      </Tooltip>
    );
  }

  return checkboxContent;
});

export default function CityForm({
  onSave,
  onCancel,
  initialData,
  isLoading,
  tripType,
  tripStartDate,
  tripEndDate,
  otherCities = [],
  permanentResidenceCountryId,
  permanentResidenceCityId,
  hasArrivalDateIssue = false,
  hasDepartureDateIssue = false,
}: CityFormProps) {
  const t = useTranslations('cityForm');
  const tValidation = useTranslations('validation');
  const overlapErrorMessage = t('cityDateOverlapError');

  // Use React Query hooks for countries and cities
  const { data: countries = [], isLoading: loadingCountries } = useCountries();
  const allowFuel = tripType !== 'international';
  const isLocalTrip = tripType === 'local';
  const permanentCountryCode = useMemo(() => {
    if (permanentResidenceCountryId == null) {
      return null;
    }
    return String(permanentResidenceCountryId);
  }, [permanentResidenceCountryId]);
  const permanentCityCode = useMemo(() => {
    if (permanentResidenceCityId == null) {
      return null;
    }
    return String(permanentResidenceCityId);
  }, [permanentResidenceCityId]);

  // Create schema with validation
  const cityFormSchema = z
    .object({
      countryId: z.string().min(1, tValidation('required')),
      cityId: z.string().min(1, tValidation('required')),
      arrivalDate: z
        .date({
          message: tValidation('required'),
        })
        .nullable(),
      departureDate: z
        .date({
          message: tValidation('required'),
        })
        .nullable(),
      needsAccommodation: z.boolean(),
      needsTransport: z.boolean(),
      needsFuel: z.boolean(),
    })
    .refine(
      (data) => !data.arrivalDate || !data.departureDate || data.departureDate >= data.arrivalDate,
      {
        path: ['departureDate'],
        message: tValidation('dateOrder') || 'Departure date must be after arrival date',
      }
    );

  const defaultDateRange = useMemo(() => {
    if (initialData || !tripStartDate || !tripEndDate) {
      return null;
    }

    const sortedRanges = [...otherCities]
      .map((city) => ({
        arrivalDate: city.arrivalDate,
        departureDate: city.departureDate,
      }))
      .sort((a, b) => a.arrivalDate.getTime() - b.arrivalDate.getTime());

    let cursor = new Date(tripStartDate.getTime());
    const endBoundary = new Date(tripEndDate.getTime());

    for (const range of sortedRanges) {
      const cityStart = range.arrivalDate < tripStartDate ? tripStartDate : range.arrivalDate;
      const cityEnd = range.departureDate > tripEndDate ? tripEndDate : range.departureDate;

      const gapEndCandidate = addDays(cityStart, -1);
      if (gapEndCandidate >= cursor && gapEndCandidate <= endBoundary) {
        return {
          arrival: new Date(cursor.getTime()),
          departure: new Date(gapEndCandidate.getTime()),
        };
      }

      const nextCursorCandidate = addDays(cityEnd, 1);
      if (nextCursorCandidate > cursor) {
        cursor = nextCursorCandidate;
      }

      if (cursor > endBoundary) {
        break;
      }
    }

    if (cursor <= endBoundary) {
      return {
        arrival: new Date(cursor.getTime()),
        departure: new Date(endBoundary.getTime()),
      };
    }

    return null;
  }, [initialData, otherCities, tripEndDate, tripStartDate]);

  const defaultValues: CityFormFields = {
    countryId: initialData?.countryId ?? (tripType === 'local' ? LOCAL_TRIP_COUNTRY_ID : ''),
    cityId: initialData?.cityId ?? '',
    arrivalDate: initialData?.arrivalDate ?? defaultDateRange?.arrival ?? tripStartDate ?? null,
    departureDate: initialData?.departureDate ?? defaultDateRange?.departure ?? tripEndDate ?? null,
    needsAccommodation: initialData?.needsAccommodation ?? false,
    needsTransport:
      isLocalTrip && initialData?.needsFuel ? false : (initialData?.needsTransport ?? false),
    needsFuel: allowFuel ? (initialData?.needsFuel ?? false) : false,
  };

  const form = useForm<CityFormFields>({
    resolver: zodResolver(cityFormSchema),
    defaultValues,
  });

  const [isArrivalDateOpen, setIsArrivalDateOpen] = useState(false);
  const [isDepartureDateOpen, setIsDepartureDateOpen] = useState(false);

  const arrivalDateValue = useWatch({ control: form.control, name: 'arrivalDate' });
  const departureDateValue = useWatch({ control: form.control, name: 'departureDate' });
  const { dirtyFields } = useFormState({
    control: form.control,
    name: ['countryId', 'arrivalDate', 'departureDate'],
  });
  const arrivalDirty = Boolean(dirtyFields?.arrivalDate);
  const departureDirty = Boolean(dirtyFields?.departureDate);
  const normalizedTripStart = useMemo(
    () => (tripStartDate ? startOfDay(tripStartDate) : null),
    [tripStartDate]
  );
  const normalizedTripEnd = useMemo(
    () => (tripEndDate ? endOfDay(tripEndDate) : null),
    [tripEndDate]
  );
  const normalizedOtherCityRanges = useMemo(
    () =>
      otherCities.map((city) => ({
        from: startOfDay(city.arrivalDate),
        to: endOfDay(city.departureDate),
      })),
    [otherCities]
  );
  const arrivalDisabledMatcher = useMemo(() => {
    if (!normalizedTripStart && !normalizedTripEnd && normalizedOtherCityRanges.length === 0) {
      return undefined;
    }

    return (date: Date) => {
      const day = startOfDay(date);
      if (normalizedTripStart && isBefore(day, normalizedTripStart)) {
        return true;
      }
      if (normalizedTripEnd && isAfter(day, normalizedTripEnd)) {
        return true;
      }

      return normalizedOtherCityRanges.some((range) =>
        isWithinInterval(day, { start: range.from, end: range.to })
      );
    };
  }, [normalizedOtherCityRanges, normalizedTripEnd, normalizedTripStart]);
  const normalizedArrivalSelected = useMemo(
    () => (arrivalDateValue ? startOfDay(arrivalDateValue) : null),
    [arrivalDateValue]
  );
  const departureDisabledMatcher = useMemo(() => {
    if (
      !normalizedTripStart &&
      !normalizedTripEnd &&
      normalizedOtherCityRanges.length === 0 &&
      !normalizedArrivalSelected
    ) {
      return undefined;
    }

    return (date: Date) => {
      const day = startOfDay(date);
      if (normalizedTripStart && isBefore(day, normalizedTripStart)) {
        return true;
      }
      if (normalizedTripEnd && isAfter(day, normalizedTripEnd)) {
        return true;
      }
      if (normalizedArrivalSelected && isBefore(day, normalizedArrivalSelected)) {
        return true;
      }

      return normalizedOtherCityRanges.some((range) =>
        isWithinInterval(day, { start: range.from, end: range.to })
      );
    };
  }, [
    normalizedArrivalSelected,
    normalizedOtherCityRanges,
    normalizedTripEnd,
    normalizedTripStart,
  ]);

  const selectedCountryId = form.watch('countryId');
  const selectedCityId = form.watch('cityId');
  const previousCountryIdRef = useRef<string | undefined>(selectedCountryId);
  const shouldLockCountry = isLocalTrip && !initialData;

  useEffect(() => {
    if (initialData) {
      return;
    }

    if (tripType === 'local') {
      const currentCountryId = form.getValues('countryId');
      if (currentCountryId !== LOCAL_TRIP_COUNTRY_ID) {
        const hasPreviousValue =
          typeof currentCountryId === 'string' && currentCountryId.trim().length > 0;
        form.setValue('countryId', LOCAL_TRIP_COUNTRY_ID, {
          shouldDirty: hasPreviousValue,
          shouldTouch: hasPreviousValue,
        });
      }
    }
  }, [form, initialData, tripType]);

  useEffect(() => {
    if (initialData) {
      return;
    }

    if (defaultDateRange && !arrivalDateValue && !arrivalDirty) {
      form.setValue('arrivalDate', defaultDateRange.arrival, {
        shouldDirty: false,
        shouldTouch: false,
      });
    } else if (tripStartDate && !arrivalDateValue && !arrivalDirty) {
      form.setValue('arrivalDate', tripStartDate, { shouldDirty: false, shouldTouch: false });
    }

    if (defaultDateRange && !departureDateValue && !departureDirty) {
      form.setValue('departureDate', defaultDateRange.departure, {
        shouldDirty: false,
        shouldTouch: false,
      });
    } else if (tripEndDate && !departureDateValue && !departureDirty) {
      form.setValue('departureDate', tripEndDate, { shouldDirty: false, shouldTouch: false });
    }
  }, [
    arrivalDateValue,
    arrivalDirty,
    departureDateValue,
    departureDirty,
    form,
    initialData,
    defaultDateRange,
    tripEndDate,
    tripStartDate,
  ]);

  useEffect(() => {
    if (!arrivalDateValue || !departureDateValue) {
      return;
    }

    const hasOverlap = otherCities.some((city) => {
      const startsAfterOtherEnds = arrivalDateValue > city.departureDate;
      const endsBeforeOtherStarts = departureDateValue < city.arrivalDate;
      return !(startsAfterOtherEnds || endsBeforeOtherStarts);
    });

    if (hasOverlap) {
      form.setError('arrivalDate', { type: 'manual', message: overlapErrorMessage });
      form.setError('departureDate', { type: 'manual', message: overlapErrorMessage });
    } else {
      const arrivalError = form.formState.errors.arrivalDate;
      const departureError = form.formState.errors.departureDate;
      if (
        arrivalError?.message === overlapErrorMessage ||
        departureError?.message === overlapErrorMessage
      ) {
        form.clearErrors(['arrivalDate', 'departureDate']);
      }
    }
  }, [arrivalDateValue, departureDateValue, form, otherCities, overlapErrorMessage]);

  useEffect(() => {
    if (
      previousCountryIdRef.current !== undefined &&
      previousCountryIdRef.current !== '' &&
      previousCountryIdRef.current !== selectedCountryId
    ) {
      form.setValue('cityId', '', { shouldDirty: true, shouldTouch: true });
    }

    previousCountryIdRef.current = selectedCountryId;
  }, [selectedCountryId, form]);

  // Use React Query hook for cities based on selected country
  const { data: cities = [], isLoading: loadingCities } = useCities(selectedCountryId);
  const isCountryDisabled = loadingCountries || shouldLockCountry;
  const isCityDisabled = !selectedCountryId || loadingCities;
  const availableCountries = useMemo(() => {
    const base =
      tripType === 'international' && permanentCountryCode
        ? countries.filter((country) => country.code !== permanentCountryCode)
        : countries;

    if (selectedCountryId && base.every((country) => country.code !== selectedCountryId)) {
      const currentSelection = countries.find((country) => country.code === selectedCountryId);
      if (currentSelection) {
        return [currentSelection, ...base];
      }
    }

    return base;
  }, [countries, permanentCountryCode, selectedCountryId, tripType]);

  const availableCities = useMemo(() => {
    const base =
      permanentCityCode != null ? cities.filter((city) => city.code !== permanentCityCode) : cities;

    if (selectedCityId && base.every((city) => city.code !== selectedCityId)) {
      const currentSelection = cities.find((city) => city.code === selectedCityId);
      if (currentSelection) {
        return [currentSelection, ...base];
      }
    }

    return base;
  }, [cities, permanentCityCode, selectedCityId]);

  // Convert cities to virtualized options format
  const cityOptions = useMemo<VirtualizedSelectOption[]>(
    () =>
      availableCities.map((city) => ({
        value: city.code,
        label: city.name,
        searchText: city.name,
      })),
    [availableCities]
  );

  // Convert countries to virtualized options format
  const countryOptions = useMemo<VirtualizedSelectOption[]>(
    () =>
      availableCountries.map((country) => ({
        value: country.code,
        label: country.name,
        searchText: country.name,
      })),
    [availableCountries]
  );

  useEffect(() => {
    if (!allowFuel) {
      const currentFuel = form.getValues('needsFuel');
      if (currentFuel) {
        form.setValue('needsFuel', false, { shouldDirty: true });
      }
    }
  }, [allowFuel, form]);

  const tripStartLimit = tripStartDate ?? null;
  const tripEndLimit = tripEndDate ?? null;

  // Determine if this is a one-day city (arrival and departure dates are the same)
  const isOneDayCity = useMemo(() => {
    if (!arrivalDateValue || !departureDateValue) {
      return false;
    }
    return isSameDay(arrivalDateValue, departureDateValue);
  }, [arrivalDateValue, departureDateValue]);

  /**
   * Checks if a single-day city can request hotel based on previous city.
   * A single-day city can request hotel if:
   * - The previous city (by date) is also a single-day city (regardless of hotel), OR
   * - The previous city has a hotel with checkout date = day before this city's arrival (connecting)
   */
  const canSingleDayCityRequestHotel = useMemo(() => {
    if (!arrivalDateValue || otherCities.length === 0) {
      return false;
    }

    // Find the city that ends the day before this city's arrival
    const dayBeforeArrival = subDays(arrivalDateValue, 1);

    const previousCity = otherCities.find((city) =>
      isSameDay(city.departureDate, dayBeforeArrival)
    );

    if (!previousCity) {
      return false; // No previous city
    }

    // If previous city is a single-day city, allow hotel request regardless of previous hotel
    const isPreviousCitySingleDay = isSameDay(previousCity.arrivalDate, previousCity.departureDate);
    if (isPreviousCitySingleDay) {
      return true;
    }

    // For multi-day previous city, check if hotel connects
    if (!previousCity.hotelCheckoutDate) {
      return false; // No hotel in previous city
    }

    // Check if previous city's hotel checkout is the day before this city's arrival
    return isSameDay(previousCity.hotelCheckoutDate, dayBeforeArrival);
  }, [arrivalDateValue, otherCities]);

  // Determine if accommodation checkbox should be disabled
  // Single-day cities can request hotel if connected to previous city's hotel
  const isAccommodationDisabledForSingleDay = isOneDayCity && !canSingleDayCityRequestHotel;

  // Auto-disable accommodation when it should be disabled for single-day city
  useEffect(() => {
    if (isAccommodationDisabledForSingleDay && form.getValues('needsAccommodation')) {
      form.setValue('needsAccommodation', false, { shouldDirty: true, shouldTouch: true });
    }
  }, [isAccommodationDisabledForSingleDay, form]);

  // Tooltip messages for disabled checkboxes
  const tooltipMessages = useMemo(
    () => ({
      oneDayTripNoHotel: t('tooltips.oneDayTripNoHotel'),
      transportFuelExclusive: t('tooltips.transportFuelExclusive'),
    }),
    [t]
  );

  const requirementConfigs = useMemo<RequirementConfig[]>(() => {
    const configs: RequirementConfig[] = [
      { key: 'needsAccommodation', label: t('needsAccommodation') },
      {
        key: 'needsTransport',
        label: t('needsTransport'),
        exclusiveWith: isLocalTrip ? 'needsFuel' : undefined,
      },
    ];

    if (allowFuel) {
      configs.push({
        key: 'needsFuel',
        label: t('needsFuel'),
        exclusiveWith: isLocalTrip ? 'needsTransport' : undefined,
      });
    }

    return configs;
  }, [allowFuel, isLocalTrip, t]);

  const handleSubmit = (data: CityFormFields) => {
    const country = countries.find((item) => item.code === data.countryId);
    const city = cities.find((item) => item.code === data.cityId);

    if (!country || !city || !data.arrivalDate || !data.departureDate) {
      return;
    }

    if (tripStartLimit && data.arrivalDate < tripStartLimit) {
      form.setError('arrivalDate', {
        type: 'manual',
        message: t('cityArrivalTripRangeError'),
      });
      return;
    }

    if (tripEndLimit && data.departureDate > tripEndLimit) {
      form.setError('departureDate', {
        type: 'manual',
        message: t('cityDepartureTripRangeError'),
      });
      return;
    }

    const overlap = otherCities.some((city) => {
      const startsAfterOtherEnds = data.arrivalDate! > city.departureDate;
      const endsBeforeOtherStarts = data.departureDate! < city.arrivalDate;
      return !(startsAfterOtherEnds || endsBeforeOtherStarts);
    });

    if (overlap) {
      form.setError('arrivalDate', { type: 'manual', message: overlapErrorMessage });
      form.setError('departureDate', { type: 'manual', message: overlapErrorMessage });
      return;
    }

    onSave({
      countryId: data.countryId,
      countryName: country.name,
      cityId: data.cityId,
      cityName: city.name,
      arrivalDate: data.arrivalDate!,
      departureDate: data.departureDate!,
      needsAccommodation: data.needsAccommodation,
      needsTransport: data.needsTransport,
      needsFuel: data.needsFuel,
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="rounded-3xl border border-[#f1f1f1] bg-white p-4 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)] md:p-5"
      >
        <div className="flex flex-col gap-4">
          {/* Header */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-[18px] leading-7 font-bold text-black">{t('cityDetails')}</h2>
          </div>

          {/* Country and City Dropdowns */}
          <div className="flex flex-col gap-4 lg:flex-row">
            {/* Country Dropdown */}
            <FormField
              control={form.control}
              name="countryId"
              render={({ field }) => {
                // Find the selected country's label for display (needed for virtualized select)
                const selectedCountryLabel =
                  countryOptions.find((opt) => opt.value === field.value)?.label ?? null;

                return (
                  <FormItem className="w-full flex-1 self-start">
                    <FormControl>
                      <div
                        className={cn(
                          'relative',
                          'aria-[invalid=true]:[&_button[data-field-border]]:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border]]:hover:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border]]:focus:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border][data-state=open]]:border-[#ff4d4f]'
                        )}
                      >
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={isCountryDisabled}
                        >
                          <SelectTrigger
                            variant="formField"
                            showIcon={false}
                            data-field-border
                            disabled={isCountryDisabled}
                            className="h-14 w-full rounded-2xl border border-[#e4e4e4] bg-white px-4 py-2 transition-colors hover:border-[#292929] focus:border-[#292929] disabled:cursor-not-allowed disabled:border-[#f1f1f1] disabled:bg-[#f7f7f7] data-[state=open]:border-[#292929]"
                          >
                            <div className="relative flex h-full w-full items-start justify-between">
                              <div className="relative flex h-full flex-1 items-center">
                                <FloatingSelectValue
                                  label={t('selectCountry')}
                                  hasValue={Boolean(field.value)}
                                  disabled={isCountryDisabled}
                                >
                                  {selectedCountryLabel}
                                </FloatingSelectValue>
                              </div>
                              <img
                                src="/assets/icons/chevron-down.svg"
                                alt=""
                                className={cn(
                                  'mt-3 h-6 w-6',
                                  isCountryDisabled ? 'opacity-40' : undefined
                                )}
                              />
                            </div>
                          </SelectTrigger>
                          <VirtualizedSelectContent
                            options={countryOptions}
                            estimatedItemHeight={52}
                            className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]"
                            itemClassName="rounded-xl px-4 py-3 text-base leading-6 text-[#292929] hover:bg-[#f9f9f9] focus:bg-[#f9f9f9] focus:text-[#292929] data-[state=checked]:bg-[#f9f9f9] data-[state=checked]:text-[#292929] [&>span.absolute]:hidden"
                          />
                        </Select>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {/* City Dropdown - Virtualized for performance with large lists */}
            <FormField
              control={form.control}
              name="cityId"
              render={({ field }) => {
                // Find the selected city's label for display (needed for virtualized select)
                const selectedCityLabel =
                  cityOptions.find((opt) => opt.value === field.value)?.label ?? null;

                return (
                  <FormItem className="w-full flex-1 self-start">
                    <FormControl>
                      <div
                        className={cn(
                          'relative',
                          'aria-[invalid=true]:[&_button[data-field-border]]:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border]]:hover:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border]]:focus:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border][data-state=open]]:border-[#ff4d4f]'
                        )}
                      >
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={isCityDisabled}
                        >
                          <SelectTrigger
                            variant="formField"
                            showIcon={false}
                            data-field-border
                            disabled={isCityDisabled}
                            className="h-14 w-full rounded-2xl border border-[#e4e4e4] bg-white px-4 py-2 transition-colors hover:border-[#292929] focus:border-[#292929] disabled:cursor-not-allowed disabled:border-[#f1f1f1] disabled:bg-[#f7f7f7] data-[state=open]:border-[#292929]"
                          >
                            <div className="relative flex h-full w-full items-start justify-between">
                              <div className="relative flex h-full flex-1 items-center">
                                <FloatingSelectValue
                                  label={t('selectCity')}
                                  placeholder={t('selectCity')}
                                  hasValue={Boolean(field.value)}
                                  disabled={isCityDisabled}
                                >
                                  {selectedCityLabel}
                                </FloatingSelectValue>
                              </div>
                              <img
                                src="/assets/icons/chevron-down.svg"
                                alt=""
                                className="mt-3 h-6 w-6"
                              />
                            </div>
                          </SelectTrigger>
                          <VirtualizedSelectContent
                            options={cityOptions}
                            estimatedItemHeight={52}
                            className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]"
                            itemClassName="rounded-xl px-4 py-3 text-base leading-6 text-[#292929] hover:bg-[#f9f9f9] focus:bg-[#f9f9f9] focus:text-[#292929] data-[state=checked]:bg-[#f9f9f9] data-[state=checked]:text-[#292929] [&>span.absolute]:hidden"
                          />
                        </Select>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>

          <div className="grid grid-cols-1 gap-4">
            {/* Arrival Date */}
            <FormField
              control={form.control}
              name="arrivalDate"
              render={({ field }) => (
                <FormItem className="space-y-2 self-start">
                  <FormLabel
                    className={cn(
                      'px-1 text-sm font-medium',
                      hasArrivalDateIssue ? 'text-[#e5484d]' : 'text-[#292929]'
                    )}
                  >
                    {t('arrivalDate')}
                  </FormLabel>
                  <Popover open={isArrivalDateOpen} onOpenChange={setIsArrivalDateOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <div
                          className={cn(
                            'flex h-14 cursor-pointer items-center justify-between rounded-2xl border px-4 py-3 transition-colors',
                            hasArrivalDateIssue
                              ? 'border-[#e5484d] focus-within:border-[#e5484d] hover:border-[#e5484d]'
                              : 'border-[#ebebeb] focus-within:border-[#292929] hover:border-[#292929]',
                            'aria-[invalid=true]:border-[#ff4d4f]',
                            'aria-[invalid=true]:hover:border-[#ff4d4f]',
                            'aria-[invalid=true]:focus-within:border-[#ff4d4f]'
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <img src="/assets/icons/calendar.svg" alt="" className="h-6 w-6" />
                            <span
                              className={cn(
                                'text-base',
                                hasArrivalDateIssue
                                  ? 'text-[#e5484d]'
                                  : field.value
                                    ? 'text-[#292929]'
                                    : 'text-[#666666]'
                              )}
                            >
                              {field.value ? format(field.value, 'dd/MM/yyyy') : t('selectDate')}
                            </span>
                          </div>
                          <img src="/assets/icons/chevron-down.svg" alt="" className="h-6 w-6" />
                        </div>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ?? undefined}
                        onSelect={(value) => {
                          field.onChange(value ?? null);
                          setIsArrivalDateOpen(false);
                        }}
                        locale={ka}
                        captionLayout="dropdown"
                        className="rounded-2xl border-[#ebebeb]"
                        defaultMonth={field.value ?? tripStartDate ?? new Date()}
                        fromDate={tripStartDate ?? undefined}
                        toDate={tripEndDate ?? undefined}
                        disabled={arrivalDisabledMatcher}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Departure Date */}
            <FormField
              control={form.control}
              name="departureDate"
              render={({ field }) => (
                <FormItem className="space-y-2 self-start">
                  <FormLabel
                    className={cn(
                      'px-1 text-sm font-medium',
                      hasDepartureDateIssue ? 'text-[#e5484d]' : 'text-[#292929]'
                    )}
                  >
                    {t('departureDate')}
                  </FormLabel>
                  <Popover open={isDepartureDateOpen} onOpenChange={setIsDepartureDateOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <div
                          className={cn(
                            'flex h-14 cursor-pointer items-center justify-between rounded-2xl border px-4 py-3 transition-colors',
                            hasDepartureDateIssue
                              ? 'border-[#e5484d] focus-within:border-[#e5484d] hover:border-[#e5484d]'
                              : 'border-[#ebebeb] focus-within:border-[#292929] hover:border-[#292929]',
                            'aria-[invalid=true]:border-[#ff4d4f]',
                            'aria-[invalid=true]:hover:border-[#ff4d4f]',
                            'aria-[invalid=true]:focus-within:border-[#ff4d4f]'
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <img src="/assets/icons/calendar.svg" alt="" className="h-6 w-6" />
                            <span
                              className={cn(
                                'text-base',
                                hasDepartureDateIssue
                                  ? 'text-[#e5484d]'
                                  : field.value
                                    ? 'text-[#292929]'
                                    : 'text-[#666666]'
                              )}
                            >
                              {field.value ? format(field.value, 'dd/MM/yyyy') : t('selectDate')}
                            </span>
                          </div>
                          <img src="/assets/icons/chevron-down.svg" alt="" className="h-6 w-6" />
                        </div>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ?? undefined}
                        onSelect={(value) => {
                          field.onChange(value ?? null);
                          setIsDepartureDateOpen(false);
                        }}
                        locale={ka}
                        captionLayout="dropdown"
                        className="rounded-2xl border-[#ebebeb]"
                        defaultMonth={
                          field.value ?? arrivalDateValue ?? tripStartDate ?? new Date()
                        }
                        fromDate={arrivalDateValue ?? tripStartDate ?? undefined}
                        toDate={tripEndDate ?? undefined}
                        disabled={departureDisabledMatcher}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Checkboxes */}
          <div className="mt-2 flex flex-col gap-2">
            {requirementConfigs.map((config) => (
              <RequirementCheckbox
                key={config.key}
                form={form}
                config={config}
                isLocalTrip={isLocalTrip}
                isAccommodationDisabledForSingleDay={isAccommodationDisabledForSingleDay}
                tooltipMessages={tooltipMessages}
              />
            ))}
          </div>

          {/* Action Buttons */}
          <div className="mt-4">
            <div className="flex flex-col gap-2 rounded-3xl bg-white p-3 shadow-[0px_-8px_24px_-8px_rgba(31,31,31,0.12)] sm:flex-row sm:items-center sm:justify-end sm:bg-transparent sm:p-0 sm:shadow-none">
              <Button
                type="button"
                variant="ghost"
                onClick={onCancel}
                className="h-auto w-full rounded-[30px] px-4 py-3 text-base font-medium text-black hover:bg-[#f9f9f9] sm:w-auto"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="flex h-auto w-full items-center justify-center gap-2 rounded-[30px] bg-[#242424] px-4 py-3 text-base font-medium text-white hover:bg-[#1a1a1a] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {isLoading && <Spinner variant="circle" className="h-4 w-4" />}
                {t('save')}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}
